import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Info, Medal, Search as SearchIcon, ShieldCheck, Trophy, Users } from "lucide-react";
import { Breadcrumb } from "../../layouts/PublicLayout";
import { Select, StatCard } from "../../components/ui";
import ClassRankingBoards, { buildBoards, ClassLeaders } from "../../components/ClassRankingBoards";
import { RankingsSidebar } from "./Rankings";
import { useApi } from "../../api/client";
import { RankingRow } from "../../data/types";
import { downloadCsv } from "../../utils/download";
import { formatAverage, formatPercent } from "../../utils/score";

/*
 * Class rankings — a board per class, and optionally per grade within a class.
 *
 * This page used to be a mock: the class was hardcoded to "Open", the counts
 * were invented, the filters did nothing and the table underneath ignored the
 * class entirely. It now reads the real ranking table, and every figure on it
 * comes from the shooters shown.
 *
 * A grade only means something inside a class — an A-grade Target Rifle shooter
 * and an A-grade F-Class shooter are not competing with each other — so the
 * grade split always sits underneath the class split, never instead of it.
 */
export default function ClassGradeRankings() {
  const [params, setParams] = useSearchParams();
  const { data: allRankings } = useApi<RankingRow[]>("/rankings", []);
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("All Classes");
  const [gradeFilter, setGradeFilter] = useState("All Grades");
  const [clubFilter, setClubFilter] = useState("All Clubs");
  /*
   * Grade Rankings is this page with the split on, so the toggle lives in the
   * URL rather than beside it. Keeping the state here instead meant ticking the
   * box put the page on the grade boards while the menu still said Class.
   */
  const splitByGrade = params.get("split") === "grade";
  const setSplitByGrade = (on: boolean) => {
    const next = new URLSearchParams(params);
    if (on) next.set("split", "grade");
    else next.delete("split");
    setParams(next, { replace: true });
  };

  /* Every option comes from the ranked shooters, so nothing is offered that
     this portal has never seen and nothing it has seen is hidden. */
  const classOptions = useMemo(
    () => Array.from(new Set(allRankings.map((r) => r.rifleClass).filter(Boolean))).sort(),
    [allRankings]
  );
  const gradeOptions = useMemo(
    () => Array.from(new Set(allRankings.map((r) => r.grade).filter(Boolean))).sort(),
    [allRankings]
  );
  const clubOptions = useMemo(
    () => Array.from(new Set(allRankings.map((r) => r.club).filter(Boolean))).sort(),
    [allRankings]
  );

  const q = search.trim().toLowerCase();
  const filtered = allRankings.filter(
    (r) =>
      (!q || r.shooter.toLowerCase().includes(q) || r.sid.includes(q)) &&
      (classFilter === "All Classes" || r.rifleClass === classFilter) &&
      (gradeFilter === "All Grades" || r.grade === gradeFilter) &&
      (clubFilter === "All Clubs" || r.club === clubFilter)
  );

  const boards = useMemo(() => buildBoards(filtered, splitByGrade), [filtered, splitByGrade]);
  const bestAverage = filtered.length ? Math.max(...filtered.map((r) => r.avgPercent ?? 0)) : 0;

  const exportCsv = () =>
    downloadCsv(
      splitByGrade ? "class-grade-rankings.csv" : "class-rankings.csv",
      ["Class", ...(splitByGrade ? ["Grade"] : []), "Rank", "Shooter", "SID", "Club", "Grade", "Events", "Overall %", "Personal Best %"],
      boards.flatMap((b) =>
        b.rows.map((r) => [
          b.rifleClass,
          ...(splitByGrade ? [b.grade ?? ""] : []),
          r.rank,
          r.shooter,
          r.sid,
          r.club,
          r.grade,
          r.competitions,
          formatPercent(r.avgPercent),
          formatPercent(r.bestPercent),
        ])
      )
    );

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-4 sm:px-5">
      <Breadcrumb
        items={[
          { label: "Home", to: "/" },
          { label: "Rankings", to: "/rankings" },
          { label: "Class Rankings" },
          ...(classFilter !== "All Classes" ? [{ label: classFilter }] : []),
        ]}
      />

      <div className="mt-3 flex flex-col gap-4 lg:flex-row">
        <RankingsSidebar title="Class Rankings" subtitle="Ranked within each class · All competitions" />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-ink">Class Rankings</h1>
              <p className="mt-1 text-[13px] text-muted">
                Each class is ranked on its own. <b className="text-ink">Target Rifle and F-TR are different disciplines</b>,
                not grades of one — different faces, different rules, 5 points a shot against 6 — so they never share a
                ladder, and a competitor entered in both is ranked in each. Grades divide a class from within, so a grade
                board always sits inside its class.
              </p>
            </div>
            <button
              onClick={exportCsv}
              className="inline-flex h-9 items-center gap-2 rounded-lg border border-line bg-white px-4 text-sm font-semibold text-ink transition-colors hover:bg-slate-50"
            >
              <Download size={15} /> Export CSV
            </button>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <StatCard icon={<ShieldCheck size={20} />} value={boards.length} label={splitByGrade ? "Class / Grade Boards" : "Classes Ranked"} />
            <StatCard icon={<Users size={20} />} value={filtered.length} label="Shooters Ranked" iconBg="bg-violet-50 text-violet-600" />
            <StatCard icon={<Medal size={20} />} value={gradeOptions.length} label="Grades In Use" iconBg="bg-warning-soft text-warning" />
            <StatCard
              icon={<Trophy size={20} />}
              value={bestAverage ? formatAverage(bestAverage) : "—"}
              label="Best Average"
              sub="Across all classes"
              iconBg="bg-success-soft text-success"
            />
          </div>

          <div className="card mt-3 flex flex-wrap items-end gap-3 p-3">
            <div className="min-w-[220px] flex-1">
              <label className="label">Search</label>
              <div className="relative">
                <SearchIcon size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  className="input pl-9"
                  placeholder="Search shooter by name or SID..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="label">Class</label>
              <Select options={["All Classes", ...classOptions]} value={classFilter} onChange={setClassFilter} className="w-full sm:w-40" />
            </div>
            <div>
              <label className="label">Grade</label>
              <Select options={["All Grades", ...gradeOptions]} value={gradeFilter} onChange={setGradeFilter} className="w-full sm:w-32" />
            </div>
            <div>
              <label className="label">Club</label>
              <Select options={["All Clubs", ...clubOptions]} value={clubFilter} onChange={setClubFilter} className="w-full sm:w-40" />
            </div>
            <label className="flex h-9 cursor-pointer items-center gap-2 text-[13px] font-semibold text-ink">
              <input
                type="checkbox"
                checked={splitByGrade}
                onChange={(e) => setSplitByGrade(e.target.checked)}
                className="h-4 w-4 accent-primary"
              />
              Split by grade
            </label>
            <button
              className="inline-flex h-9 items-center rounded-lg px-3 text-[13px] font-semibold text-muted transition-colors hover:bg-slate-100 hover:text-ink"
              onClick={() => {
                setSearch("");
                setClassFilter("All Classes");
                setGradeFilter("All Grades");
                setClubFilter("All Clubs");
                setSplitByGrade(false);
              }}
            >
              Reset
            </button>
          </div>

          <div className="mt-3">
            <ClassRankingBoards rows={filtered} byGrade={splitByGrade} emptyMessage="No shooters match the current filters." />
          </div>
        </div>

        <aside className="w-full shrink-0 space-y-4 lg:w-[268px]">
          <ClassLeaders rows={allRankings} />
          <div className="card p-4">
            <h4 className="flex items-center gap-2 text-sm font-extrabold text-ink">
              <Info size={15} className="text-primary" /> How this works
            </h4>
            <ul className="mt-2 space-y-2 text-[13px] leading-snug text-muted">
              <li>Shooters are ranked on average score, then best score.</li>
              <li>A competitor entered in two classes is ranked in each, on the results they shot in that class.</li>
              <li>Grades divide a class, so grade boards sit inside their class.</li>
              <li>Rankings are recalculated after every import.</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
